import React, { useEffect, useState } from "react";
import { TransitionLink } from "gatsby-plugin-transition-link/components/TransitionLink";
import { CSSTransition } from "react-transition-group";
import { Link } from "gatsby";
import Layout from "../components/Layout";
import PropTypes from "prop-types";
import { graphql } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";

import InfoBox from "../components/share/InfoBox/InfoBox";
import AnnouncementPopup from "../components/share/AnnouncementPopup/AnnouncementPopup";
import "./index-page.scss";
import useBreakpoint from "../hooks/useBreakpoint";

export const IndexPageTemplate = ({ links, infoBox }) => {
  const [imageLayer, setImageLayer] = useState(true);
  const [menuLayer, setMenuLayer] = useState(false);
  const BreakpointM = useBreakpoint("m");
  const { showInfoBox, title, text, introText } = infoBox || {};

  const startAnimation = () => {
    if (!imageLayer) return;
    setImageLayer(false);
    setTimeout(() => {
      setMenuLayer(true);
    }, 400);
  };

  useEffect(() => {
    window.addEventListener("wheel", startAnimation, { once: true });
    window.addEventListener("touchmove", startAnimation, { once: true });
    return () => {
      window.removeEventListener("wheel", startAnimation);
      window.removeEventListener("touchmove", startAnimation);
    };
  }, []);

  return (
    <div
      className="IndexPage"
      onClick={() => startAnimation()}
      onKeyDown={() => startAnimation()}
      onWheel={() => startAnimation()}
      onTouchMove={() => startAnimation()}
    >
      <h1 className="IndexPage__seo-title">
        Poolbau &amp; Schwimmbadbau in Hannover – Heinze Pool
      </h1>
      <AnnouncementPopup />
      <div className="IndexPage__content">
        <div className="IndexPage__bg-image">
          <CSSTransition
            in={!imageLayer}
            timeout={300}
            classNames="fadeIn"
            mountOnEnter
          >
            <StaticImage
              src="../img/water.webp"
              width={2300}
              className="IndexPage__image"
              placeholder="none"
            />
          </CSSTransition>
        </div>
        {!imageLayer &&
          (BreakpointM ? (
            <>
              <div className="IndexPage__mobile">
                {menuLayer && showInfoBox && (
                  <InfoBox title={title} text={text} introText={introText} />
                )}
                <div className="IndexPage__box">
                  <div className={`IndexPage__logo-box`}>
                    <StaticImage
                      src="../img/hp-logo_white.png"
                      width={360}
                      alt="Heinze-Pool"
                      placeholder="none"
                    />
                  </div>
                </div>
                {links.map((link, index) => {
                  return (
                    <Link
                      key={index}
                      className="IndexPage__link"
                      to={link.path}
                    >
                      {link.label}
                    </Link>
                  );
                })}
              </div>
            </>
          ) : (
            <CSSTransition
              in={menuLayer}
              timeout={300}
              classNames="fadeIn"
              unmountOnExit
              mountOnEnter
            >
              <div className={`IndexPage__wrapper`}>
                <div className={`IndexPage__menu-left`}>
                  {links &&
                    links.map((link, index) => {
                      if (index < 3) {
                        return (
                          <TransitionLink
                            key={index}
                            className="IndexPage__link"
                            to={link.path}
                          >
                            {link.label}
                          </TransitionLink>
                        );
                      } else return null;
                    })}
                </div>
                <div className={`IndexPage__menu-right`}>
                  {links &&
                    links.map((link, index) => {
                      if (index >= 3) {
                        return (
                          <TransitionLink
                            key={index}
                            className="IndexPage__link"
                            to={link.path}
                          >
                            {link.label}
                          </TransitionLink>
                        );
                      } else return null;
                    })}
                </div>
                <div className={`IndexPage__logo-box`}>
                  <StaticImage
                    src="../img/hp-logo_white.png"
                    width={600}
                    alt="Heinze-Pool"
                    placeholder="none"
                    layout="constrained"
                  />
                </div>
                <div className="IndexPage__hexa-box">
                  <StaticImage
                    src="../img/hexagon_line.png"
                    width={810}
                    alt="Heinze-Pool"
                    placeholder="none"
                  />
                </div>
                {menuLayer && showInfoBox && (
                  <InfoBox title={title} text={text} introText={introText} />
                )}
              </div>
            </CSSTransition>
          ))}
      </div>
      <CSSTransition
        in={imageLayer}
        timeout={400}
        classNames="fadeIn-late"
        unmountOnExit
      >
        <div className={`IndexPage__intro`}>
          <StaticImage
            className="IndexPage__image"
            src="../img/Start.webp"
            width={2800}
            alt="Harz Pool"
            placeholder="none"
          />
        </div>
      </CSSTransition>
      <CSSTransition
        in={imageLayer}
        timeout={300}
        classNames="moveup"
        unmountOnExit
        mountOnEnter
      >
        <div className="IndexPage__logo">
          <StaticImage
            src="../img/hp-logo_white-sub.png"
            width={400}
            alt="Heinze-Pool"
            placeholder="none"
          />
        </div>
      </CSSTransition>
    </div>
  );
};

IndexPageTemplate.propTypes = {
  links: PropTypes.array,
  infoBox: PropTypes.object,
};

const IndexPage = ({ data, location }) => {
  const { frontmatter } = data.markdownRemark;
  return (
    <Layout isIndex={true} pageTitle={frontmatter.metaTitle} pageDescription={frontmatter.metaDescription} pagePath={location && location.pathname}>
      <IndexPageTemplate
        links={frontmatter.links}
        infoBox={frontmatter.infoBox}
      />
    </Layout>
  );
};

IndexPage.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.shape({
      frontmatter: PropTypes.object,
    }),
  }),
};

export default IndexPage;

export const pageQuery = graphql`
  query IndexPageTemplate {
    markdownRemark(frontmatter: { templateKey: { eq: "index-page" } }) {
      frontmatter {
        links {
          label
          path
        }
        infoBox {
          showInfoBox
          title
          text
          introText
        }
        metaTitle
        metaDescription
      }
    }
  }
`;
